import { XIcon, FileText } from "lucide-react";
import { IMGUPLOAD } from "icons";
import { Icon } from "components";
import { useFileUpload } from "ui/hooks/use-file-upload";
import { Button } from "ui/components/button";
import { useState, useEffect } from "react";

export default function FileUpload({
  className = "",
  objectFit = "cover",
  value,
  onChange,
  accept = "image/*,video/*,application/pdf",
}) {
  const [imageError, setImageError] = useState(false);
  const [{ files }, { removeFile, openFileDialog, getInputProps }] =
    useFileUpload({
      accept: accept,
      onFilesChange: (selectedFiles) => {
        if (onChange) onChange(selectedFiles[0] || null);
        setImageError(false); // Reset error when new file is selected
      },
    });

  const baseUrl =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_BASE_URL) ||
    process.env.NEXT_PUBLIC_BASE_URL ||
    "";

  const previewUrl =
    files[0]?.preview ||
    (typeof value === "string" && value
      ? `${baseUrl}/assets/${value}`
      : null);
  const fileName = files[0]?.file.name || null;
  const fileType = files[0]?.file.type || "";
  const isImage = fileType.startsWith("image/");
  const isVideo = fileType.startsWith("video/");
  const isPdf =
    fileType === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf");

  // For existing files (value is string), try to show as image by default
  const isExistingFile = typeof value === "string" && value && !files[0]?.file;

  // Reset image error when value or files change
  useEffect(() => {
    setImageError(false);
  }, [value, files]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative size-44">
        {previewUrl ? (
          <>
            {isVideo && (
              <video
                className="size-full"
                src={previewUrl}
                controls
                style={{ objectFit }}
              >
                Your browser does not support the video tag.
              </video>
            )}
            {isPdf && (
              <div className="size-full flex items-center justify-center bg-gray-100 border border-gray-200 rounded">
                <FileText className="size-12 text-gray-400" />
              </div>
            )}
            {(isImage || (isExistingFile && !isVideo && !isPdf)) &&
              !imageError && (
                <img
                  className="size-full"
                  src={previewUrl}
                  alt="Preview of uploaded file"
                  width={64}
                  height={64}
                  style={{ objectFit }}
                  onError={() => setImageError(true)}
                />
              )}
            {imageError && (
              <div className="size-full flex items-center justify-center bg-gray-100 border border-gray-200 rounded">
                <FileText className="size-12 text-gray-400" />
              </div>
            )}
            <Button
              onClick={() => {
                removeFile(files[0]?.id);
                if (onChange) onChange(null);
              }}
              size="icon"
              className="absolute top-1 right-1 size-6 rounded-full bg-white/40 hover:bg-white shadow"
              aria-label="Remove file"
              style={{ zIndex: 2 }}
            >
              <XIcon className="size-3.5 text-black" />
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            className={`relative size-16 overflow-hidden p-0 shadow-none rounded-none ${className}`}
            onClick={openFileDialog}
            aria-label="Upload file"
          >
            <div aria-hidden="true">
              <Icon
                icon={IMGUPLOAD}
                className="size-13 text-muted-foreground opacity-30"
              />
            </div>
          </Button>
        )}
        <input
          {...getInputProps()}
          className="sr-only"
          aria-label="Upload file"
          tabIndex={-1}
        />
      </div>
      {fileName && (
        <p className="text-muted-foreground text-xs truncate">{fileName}</p>
      )}
    </div>
  );
}
